import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Plus } from "lucide-react";
import { useHistory } from "react-router-dom";
import Customer from "../../../actions/customer";
import Order from "../../../actions/order";
import Product from "../../../actions/product";
import Controls from "../../../components/controls/Controls";
import AddCustomerForm from "../../../components/forms/addCustomer";
import { Form, useForm } from "../../../components/customHelpers/useForm";
import Layout from "../../../components/layout/Layout";
import Admin from "../../../components/auth/Admin";
import Notification from "../../../components/customHelpers/Notification";
import Notify from "../../../components/customHelpers/Notify";
import OrderTable from "./OrderTable";
import Popup from "../../../components/customHelpers/Popup";
import CustomerDetails from "./customerDetail";
import ConfirmDialog from "../../../components/customHelpers/ConfirmDialog";
import { updatePrices } from "../../../utils/utils";
import CustomerOrder from "./CustomerOrders";
import { useOrderManagement } from "./addOrderManagement";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoComponent from "../../../components/InfoComponent";
import { formatNumber } from "../../../utils/utils";
import { Search } from "@mui/icons-material";

const SelectedProductDetails = ({ product }) => {
  if (!product) return null;
  return (
    <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-md p-1.5 shadow-sm mb-2">
      <h3 className="text-sm font-semibold text-green-900 mb-1 border-b border-green-200 pb-0.5">
        Selected Product
      </h3>
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-gray-600 min-w-[35px]">
            Name:
          </span>
          <span className="text-sm text-gray-900 font-medium">
            {product.product?.name}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-gray-600 min-w-[35px]">
            SKU:
          </span>
          <span className="text-sm text-gray-900">{product.product?.sku}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-gray-600 min-w-[35px]">
            Stock:
          </span>
          <span className="text-sm text-gray-900">{product.quantity}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-gray-600 min-w-[35px]">
            Store:
          </span>
          <span className="text-sm text-gray-900">
            {product.store.locationName}
          </span>
        </div>
      </div>
    </div>
  );
};

const statusLists = ["Ordered", "Pending"];
const initialFValues = {
  editing: false,
};

const AddOrder = () => {
  const history = useHistory();
  const { NotifyMessage, notify, setNotify } = Notify();
  const { viewAllCustomers } = Customer();
  const { viewAllProductsLists, updateProductList } = Product();
  const { addOrder } = Order();
  const {
    orders,
    filterOrdersByProductId,
    groupByStatusAndSumQuantities,
    addPendingOrderLocally,
    removePendingOrderLocally,
  } = useOrderManagement();

  const [products, setProducts] = useState([]);
  const [tempProducts, setTempProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warning, setWarning] = useState("");
  const [remark, setRemark] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [data, setData] = useState([]);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [edite, setEdit] = useState(false);
  const [productName, setProductName] = useState();
  const [store, setStore] = useState();
  const [sku, setSku] = useState();
  const [customerName, setCustomerName] = useState(null);
  const [paid, setPaid] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);
  const [unpaid, setUnpaid] = useState(0);
  const [productId, setProdcutId] = useState();
  const [price, setPrice] = useState("0");
  const [qt, setQt] = useState(0);
  const [quantityError, setQuantityError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isPercentDiscount, setIsPercentDiscount] = useState(false);
  const [productQuantity, setProductQuantity] = useState(0);
  const customerDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);

  const { values, setValues } = useForm(initialFValues, true, () => {});

  useEffect(() => {
    getAllCustomers();
    getAllProducts();
  }, []);

  useEffect(() => {
    const totalPrice = calculateTotalPrice();
    setPaid(totalPrice);
    setUnpaid(0);
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setFilteredCustomers([]);
      }
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setFilteredProducts([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getAllProducts = () => {
    viewAllProductsLists().then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.product) {
        setProducts(updatePrices(data.product));
        setTempProducts(updatePrices(data.product));
      }
    });
  };

  const getAllCustomers = () => {
    setValues({ ...values, loading: true });
    viewAllCustomers().then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.customers) {
        setValues({
          ...values,
          loading: false,
        });
        setCustomers(data.customers);
      }
    });
  };

  const handleSearchProductInputChange = (event) => {
    const input = event.target.value;
    setInputValue(input);
    if (input) {
      const filtered = products.filter(
        (data) =>
          data.product?.name.toLowerCase().includes(input.toLowerCase()) ||
          data.product?.sku.toLowerCase().includes(input.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products.filter((option) => option.quantity > 0));
    }
  };

  const handleProductInputFocus = () => {
    setFilteredProducts(products.filter((option) => option.quantity > 0));
  };

  const handleSearchCustomerInputChange = (event) => {
    const input = event.target.value;
    setCustomerInputValue(input);
    if (input) {
      const filtered = customers.filter((customer) =>
        customer.fullName.toLowerCase().includes(input.toLowerCase())
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  };

  const handleCustomerInputFocus = () => {
    setFilteredCustomers(customers);
  };

  const handleChangeQt = (e) => {
    const value = Number(e.target.value);
    if (productQuantity && value > productQuantity) {
      setQuantityError("Quantity exceeds available stock");
    } else {
      setQuantityError("");
    }
    setQt(e.target.value);
  };

  const onChangeProductList = (option) => {
    if (option) {
      setProductName(option.product.name);
      setProdcutId(option.id);
      setPrice(option.price);
      setProductQuantity(option.quantity);
      setStore(option.store?.locationName);
      setSku(option.product.sku);
      setSelectedProduct(option);
      setInputValue("");
      setFilteredProducts([]);
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer.id);
    setCustomerName(customer);
    setCustomerInputValue("");
    setFilteredCustomers([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let orderData = {
      items: data,
      remark: remark,
      paid: unpaid ? paid : calculateTotalPrice(),
      customerID: selectedCustomer,
      status: selectedStatus ? selectedStatus : "Ordered",
      totalPrice: calculateTotalPrice(),
      totalQuantity: data?.reduce(
        (total, list) => total + parseInt(list.quantity),
        0
      ),
    };
    setValues({ ...values, submitting: true });

    addOrder(orderData).then((data) => {
      if (data.err) {
        setValues({ ...values, submitting: false });
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.order) {
        history.push(`/orders`);
        NotifyMessage({
          message: "Order successfully created",
          type: "success",
        });
      }
    });
  };

  const handleCache = () => {
    let item;
    const existingProductIndex = data.findIndex(
      (item) => item.product === productId
    );

    const quantityToAdd = Number(qt);

    if (existingProductIndex !== -1) {
      const updatedData = data.map((item, index) =>
        index === existingProductIndex
          ? {
              ...item,
              quantity: item.quantity + quantityToAdd,
              totalPrice: (item.quantity + quantityToAdd) * item.price,
            }
          : item
      );
      item = updatedData;
    } else {
      let totalPrice = quantityToAdd * price;
      if (discount > 0) {
        totalPrice = calculateItemTotal.toFixed(2);
      }
      item = [
        ...data,
        {
          product: productId,
          store: store,
          sku: sku,
          price: price,
          totalPrice: totalPrice,
          quantity: quantityToAdd,
          productName: productName,
        },
      ];
    }

    let orderData = {
      items: item,
      remark: remark,
      paid: unpaid ? paid : calculateTotalPrice(),
      customerID: selectedCustomer,
      status: selectedStatus ? selectedStatus : "Ordered",
      totalPrice: calculateTotalPrice(),
      totalQuantity: item.reduce(
        (total, list) => total + parseInt(list.quantity),
        0
      ),
    };
    setValues({ ...values, submitting: true });

    addOrder(orderData).then((data) => {
      if (data.err) {
        setValues({ ...values, submitting: false });
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.order) {
        history.push(`/orders`);
        NotifyMessage({
          message: "Order successfully created",
          type: "success",
        });
      }
    });
  };

  const updateProductPrice = () => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false,
    });
    updateProductList({ price: price }, selectedProduct.id).then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else {
        NotifyMessage({
          message: "Product Price successfully updated",
          type: "success",
        });
        updatePrice(selectedProduct.id, price);
      }
    });
  };

  const updatePrice = (id, newPrice) => {
    setTempProducts((tempProducts) =>
      tempProducts.map((item) =>
        item.id === id ? { ...item, price: newPrice } : item
      )
    );
    setProducts((products) =>
      products.map((item) =>
        item.id === id ? { ...item, price: newPrice } : item
      )
    );
  };

  const saveData = () => {
    if (selectedProduct.price !== price) {
      setConfirmDialog({
        isOpen: true,
        title: "Are you sure to update the inventory price?",
        onConfirm: () => {
          updateProductPrice();
        },
      });
    }
    const quantityToAdd = Number(qt);
    const existingProductIndex = data.findIndex(
      (item) =>
        item.product === productId &&
        Number(item.price.trim()) === Number(price.trim()) &&
        Number(item.discount) === Number(discountValue)
    );
    const existingItem = data.find(
      (item) =>
        item.product === productId &&
        Number(item.price.trim()) === Number(price.trim()) &&
        Number(item.discount) === Number(discountValue)
    );
    if (existingItem) {
      const updatedData = data.map((item, index) =>
        index === existingProductIndex
          ? {
              ...item,
              quantity: item.quantity + quantityToAdd,
              totalPrice:
                discount > 0
                  ? (item.price - item.discount) *
                    (item.quantity + quantityToAdd)
                  : (item.quantity + quantityToAdd) * item.price,
              status: "Pending",
            }
          : item
      );
      setData(updatedData);
    } else {
      let totalPrice = quantityToAdd * price;
      if (discount > 0) {
        totalPrice = calculateItemTotal.toFixed(2);
      }

      setData([
        ...data,
        {
          product: productId,
          discount: discountValue,
          store: store,
          sku: sku,
          price: price,
          totalPrice: totalPrice,
          quantity: quantityToAdd,
          productName: productName,
        },
      ]);
    }
    addPendingOrderLocally({
      sku,
      quantity: quantityToAdd,
      status: "Pending",
    });
    const updatedProducts = products.map((product) =>
      product.id === productId
        ? { ...product, quantity: product.quantity - quantityToAdd }
        : product
    );
    setProducts(updatedProducts);
    setPrice("0");
    setQt(0);
    setProdcutId(null);
    setProductName(null);
    setSelectedProduct(null);
    setInputValue("");
    setDiscount(0);
    setFilteredProducts([]);
  };

  const calculateTotalPrice = () => {
    let totalPrice = 0;
    if (data.length > 0) {
      data.forEach((order) => {
        totalPrice += parseFloat(order.totalPrice);
      });
      return totalPrice.toFixed(2);
    }
    return "0.00";
  };

  const handlePaidChange = (event) => {
    const newPaid = event.target.value;
    if (/^\d*\.?\d*$/.test(newPaid)) {
      setPaid(newPaid);
      const remaining = calculateTotalPrice() - parseFloat(newPaid || 0);
      setUnpaid(remaining);
    }
  };

  const calculateItemTotal = useMemo(() => {
    if (isPercentDiscount) {
      const totalPriceWithoutDiscount = price * qt;
      const discountAmount = (price * discount * qt) / 100;
      const netDiscount = (price * discount) / 100;
      setDiscountValue(netDiscount);
      return totalPriceWithoutDiscount - discountAmount;
    } else {
      setDiscountValue(discount);
      return (price - discount) * qt;
    }
  }, [isPercentDiscount, discount, price, qt]);

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleDiscountChange = (e) => {
    const newDiscount = Number(e.target.value);
    setDiscount(e.target.value);
    setWarning("");
    if (isPercentDiscount) {
      if (newDiscount > 100) {
        setWarning("Discount percentage cannot exceed 100%.");
      }
    } else if (newDiscount > price) {
      setWarning("Discount cannot exceed item price.");
    }

    if (isPercentDiscount) {
      const discountAmount = (price * newDiscount) / 100;
      setDiscountValue(discountAmount);
    } else {
      setDiscountValue(newDiscount);
    }
  };

  const handleChange = (event) => {
    setRemark(event.target.value);
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const filteredResults = filterOrdersByProductId(sku);
  const summedResults = groupByStatusAndSumQuantities(filteredResults);

  return (
    <Layout>
      <Admin>
        <div
          className="card"
          style={{
            marginBottom: 0,
            paddingBottom: 0,
            borderRadius: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">
              <ArrowBackIcon
                style={{ cursor: "pointer" }}
                onClick={() => {
                  history.push("/orders");
                }}
              ></ArrowBackIcon>{" "}
              <span style={{ marginLeft: "6px" }}>Create Order Page</span>
            </Typography>
            <Controls.Button
              text="Add Customer"
              variant="outlined"
              startIcon={<Plus className="w-3 h-3" />}
              className="newButton"
              onClick={() => {
                setOpenPopup(true);
                setRecordForEdit(null);
                setEdit(false);
              }}
            />
          </Box>

          <InfoComponent pageKey="createOrder" />
        </div>

        <div className="bg-white p-3 mb-2 shadow-sm">
          <h2 className="text-sm font-semibold text-blue-900 ml-2 mb-1.5">
            Customer Selection
          </h2>
          <div className="flex flex-col lg:flex-row gap-2">
            <div
              className="search-header-container"
              style={{ marginBottom: 0, flex: 1, maxWidth: 260, minWidth: 260 }}
              ref={customerDropdownRef}
            >
              <div className="search-header-flex">
                <div className="search-container">
                  <Search className="search_icon" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={customerInputValue}
                    onChange={handleSearchCustomerInputChange}
                    onFocus={handleCustomerInputFocus}
                    className="search-input"
                    style={{ width: "100%" }}
                  />
                  {filteredCustomers.length > 0 && (
                    <ul className="absolute z-20 mt-1 max-h-40 w-full max-w-[260px] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 border border-gray-200">
                      {filteredCustomers.map((customer) => (
                        <li
                          key={customer.id}
                          className="cursor-pointer select-none py-1.5 px-2 hover:bg-blue-50 hover:text-blue-700 text-gray-900 border-b border-gray-100 last:border-b-0"
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-xs">
                              {customer.fullName} -
                            </div>
                            <div className="text-xs text-gray-500">
                              {customer?.phoneNo}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {customerName && (
              <div className="lg:w-3/4 flex flex-col gap-1.5 lg:mr-20">
                <CustomerDetails customer={customerName} />
                <CustomerOrder id={customerName.id} />
              </div>
            )}
          </div>
        </div>
        <OrderTable
          productLists={data}
          setData={setData}
          paid={paid}
          setPaid={setPaid}
          setProducts={setProducts}
          tempProducts={tempProducts}
          products={products}
          removePendingOrderLocally={removePendingOrderLocally}
        />
        <div className="bg-white border border-gray-200 rounded-md shadow-sm p-3">
          <h2 className="text-sm font-semibold text-blue-900 mb-2 border-b border-gray-200 pb-2">
            Order Details
          </h2>
          <Form onSubmit={handleSubmit}>
            <Notification notify={notify} setNotify={setNotify} />
            <div className="flex flex-col [@media(min-width:1180px)]:flex-row gap-3">
              <div
                className="search-header-container"
                style={{
                  marginBottom: 0,
                  flex: 1,
                  maxWidth: 260,
                  minWidth: 260,
                }}
                ref={productDropdownRef}
              >
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Select Product
                </h3>
                <div className="search-header-flex">
                  <div className="search-container">
                    <Search className="search_icon" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={inputValue}
                      onChange={handleSearchProductInputChange}
                      onFocus={handleProductInputFocus}
                      className="search-input"
                      style={{ width: "100%" }}
                    />
                    {filteredProducts.length > 0 && (
                      <ul className="absolute z-20 mt-1 max-h-40 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 border border-gray-200">
                        {(filteredProducts.length === 0
                          ? products.filter((option) => option.quantity > 0)
                          : filteredProducts.filter(
                              (option) => option.quantity > 0
                            )
                        ).map((option) => (
                          <li
                            key={option.id}
                            className="cursor-pointer select-none py-1.5 px-2 hover:bg-blue-50 hover:text-blue-700 text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => onChangeProductList(option)}
                          >
                            <div className="font-medium text-xs">
                              {`${option.product?.name} - ${
                                option.product?.sku
                              } - ${option.quantity} - ${
                                option.store?.locationName
                                  ? option.store?.locationName
                                  : ""
                              }`}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="[@media(min-width:1180px)]:w-1/2">
                <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Product Details
                  </h3>

                  <SelectedProductDetails product={selectedProduct} />

                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Order Status
                    </label>
                    <div className="bg-white rounded-md border border-gray-200 p-1.5">
                      {selectedProduct && summedResults.length > 0 ? (
                        summedResults.map((result, index) => (
                          <div
                            key={index}
                            className="flex justify-start items-center text-xs gap-x-10"
                          >
                            <span className="text-gray-600">
                              {result.status}:
                            </span>
                            <span className="font-medium text-gray-900">
                              {result.totalQuantity}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-gray-500">
                          <div className="flex justify-start items-center text-xs gap-x-10">
                            <span>Ordered:</span>
                            <span>0</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Pending:</span>
                            <span>0</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        value={qt}
                        onChange={handleChangeQt}
                        disabled={!selectedProduct}
                        className={`w-24 border ${
                          quantityError ? "border-red-500" : "border-gray-300"
                        } rounded-md p-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500`}
                        placeholder="0"
                      />
                      {quantityError && (
                        <p className="text-red-500 text-xs mt-0.5">
                          {quantityError}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Unit Price
                      </label>
                      <input
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        disabled={!selectedProduct}
                        className="w-24 border border-gray-300 rounded-md p-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Discount
                      </label>
                      <input
                        type="number"
                        value={discount}
                        onChange={handleDiscountChange}
                        disabled={!selectedProduct}
                        className={`w-24 border ${
                          warning ? "border-red-500" : "border-gray-300"
                        } rounded-md p-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500`}
                        placeholder="0"
                      />
                      {warning && (
                        <p className="text-red-500 text-xs mt-0.5">{warning}</p>
                      )}
                      <label className="flex items-center gap-1 mt-1">
                        <input
                          type="checkbox"
                          checked={isPercentDiscount}
                          onChange={(e) =>
                            setIsPercentDiscount(e.target.checked)
                          }
                          disabled={!selectedProduct}
                          className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                        />
                        <span className="text-xs text-gray-700">Percent</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Total Price
                      </label>
                      <input
                        type="text"
                        value={
                          qt > 0 && price && selectedProduct
                            ? discount > 0
                              ? calculateItemTotal.toFixed(2)
                              : (price * qt).toFixed(2)
                            : "0.00"
                        }
                        disabled
                        className="w-24 border border-gray-300 rounded-md p-1.5 text-xs bg-gray-100 text-gray-600"
                      />
                    </div>
                  </div>

                  <div className="mt-2">
                    <Controls.Button
                      text="Add Item"
                      size="small"
                      variant="contained"
                      startIcon={<Plus className="w-3 h-3" />}
                      disabled={!selectedProduct || qt <= 0 || quantityError}
                      className={`w-24 bg-green-600 hover:bg-green-700 text-white px-2 py-1.5 rounded-md flex items-center justify-center gap-1 text-xs font-medium disabled:bg-gray-400 disabled:cursor-not-allowed`}
                      onClick={saveData}
                    />
                  </div>
                </div>
              </div>

              <div className="[@media(min-width:1180px)]:w-1/4 space-y-2">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Order Notes & Status
                </h3>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Order Notes
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Remark"
                    value={remark}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-1.5 text-xs resize-vertical focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {calculateTotalPrice() && (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Total Price
                      </label>
                      <div className="text-sm font-bold text-gray-900">
                        {formatNumber(calculateTotalPrice())}
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Paid Amount
                        </label>
                        <input
                          type="number"
                          value={paid}
                          onChange={handlePaidChange}
                          className="w-full border border-gray-300 rounded-md p-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Balance
                        </label>
                        <input
                          type="text"
                          value={unpaid}
                          disabled
                          className="w-full border border-gray-300 rounded-md p-1.5 text-xs bg-gray-100 text-gray-600"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Order Status
                    </label>
                    <select
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      className="w-full border border-gray-300 rounded-md p-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="" disabled>
                        Select Status
                      </option>
                      {statusLists.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 mt-4 sm:mt-0">
                    {productId || selectedProduct ? (
                      <Controls.Button
                        disabled={
                          values.submitting ||
                          selectedCustomer === null ||
                          data.length === 0
                        }
                        text={
                          values.submitting ? "Processing..." : "Create Order"
                        }
                        variant="contained"
                        className={`w-full py-1.5 rounded-md text-xs text-white font-medium ${
                          values.submitting ||
                          selectedCustomer === null ||
                          data.length === 0
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                        } transition-colors`}
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            title: "Are you sure to Add this Order?",
                            subTitle: "There is A Product that is not added",
                            onConfirm: () => {
                              handleCache();
                            },
                          });
                        }}
                      />
                    ) : (
                      <Controls.Button
                        disabled={
                          values.submitting ||
                          selectedCustomer === null ||
                          data.length === 0
                        }
                        text={
                          values.submitting ? "Processing..." : "Create Order"
                        }
                        variant="contained"
                        className={`w-full py-1.5 rounded-md text-xs text-white font-medium ${
                          values.submitting ||
                          selectedCustomer === null ||
                          data.length === 0
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                        } transition-colors`}
                        onClick={handleSubmit}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Form>
        </div>

        <Notification notify={notify} setNotify={setNotify} />
        <Popup
          title="Add New Customer"
          openPopup={openPopup}
          setOpenPopup={setOpenPopup}
        >
          <AddCustomerForm
            NotifyMessage={NotifyMessage}
            setOpenPopup={setOpenPopup}
            recordForEdit={recordForEdit}
            setCustomer={setCustomers}
            setCustomerName={setCustomerName}
            fromOrder={true}
            edite={edite}
            setSelectedCustomer={setSelectedCustomer}
          />
        </Popup>
        <ConfirmDialog
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
          close={handleSubmit}
        />
      </Admin>
    </Layout>
  );
};

export default AddOrder;
